import * as React from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, Checkbox, Col, cn, uuid } from '@rematter/ui-web';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import { LucidePlus, LucideRotateCcw } from 'lucide-react';

import { DefaultPhotoRequirement } from '../../_graphql/queryDefaultPhotoRequirements';
import { PhotoRequirementRow } from '../_shared/PhotoRequirementRow';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

export type PhotoRequirement = {
  id: string;
  description: string;
  required: boolean;
};

interface Props {
  validationErrors: Record<string, string>;
  useAsDefault: boolean;
  onUseAsDefaultChange: (checked: boolean) => void;
  photoRequirements: PhotoRequirement[];
  defaultPhotoRequirements?: DefaultPhotoRequirement[];
  onPhotoRequirementsChange: React.Dispatch<
    React.SetStateAction<PhotoRequirement[]>
  >;
  isDisabled?: boolean;
  className?: string;
}

export const BookingPhotoRequirements: React.FC<Props> = ({
  className,
  validationErrors,
  useAsDefault,
  onUseAsDefaultChange,
  photoRequirements,
  defaultPhotoRequirements,
  onPhotoRequirementsChange,
  isDisabled,
}) => {
  const t = useTranslations();

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const hasInitialized = React.useRef(false);

  const isDefaultPhotoRequirementsSet =
    !!defaultPhotoRequirements && defaultPhotoRequirements.length > 0;

  const isSameAsDefault = React.useMemo(
    () =>
      defaultPhotoRequirements &&
      defaultPhotoRequirements.length > 0 &&
      isEqual(
        orderBy(defaultPhotoRequirements, ['order'], ['asc']).map((req) => ({
          name: req.name,
          isRequired: req.isRequired,
          order: req.order,
        })),
        photoRequirements
          .filter(
            (req) => req.description != null && req.description.trim() !== '',
          )
          .map((req, index) => ({
            name: req.description,
            isRequired: req.required,
            order: index,
          })),
      ),
    [defaultPhotoRequirements, photoRequirements],
  );

  const applyDefaultPhotoRequirements = React.useCallback(() => {
    const mappedRequirements =
      orderBy(defaultPhotoRequirements, ['order'], ['asc'])?.map((req) => ({
        id: req.id,
        description: req.name,
        required: req.isRequired,
      })) ?? [];
    mappedRequirements.push({
      id: uuid(),
      description: '',
      required: false,
    });
    onPhotoRequirementsChange(mappedRequirements);
  }, [defaultPhotoRequirements, onPhotoRequirementsChange]);

  const addPhotoRequirement = React.useCallback(() => {
    onPhotoRequirementsChange((prev) => [
      ...prev,
      { id: uuid(), description: '', required: false },
    ]);
  }, [onPhotoRequirementsChange]);

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        onPhotoRequirementsChange((prev) => {
          const oldIndex = prev.findIndex((item) => item.id === active.id);
          const newIndex = prev.findIndex((item) => item.id === over?.id);
          if (oldIndex === -1 || newIndex === -1) return prev;

          const reordered = arrayMove(prev, oldIndex, newIndex);
          if (newIndex === prev.length - 1) {
            reordered.push({
              id: uuid(),
              description: '',
              required: false,
            });
          }
          return reordered;
        });
      }
    },
    [onPhotoRequirementsChange],
  );

  const removePhotoRequirement = React.useCallback(
    (index: number) => {
      onPhotoRequirementsChange((prev) => prev.filter((_, i) => i !== index));
    },
    [onPhotoRequirementsChange],
  );

  const updatePhotoRequirement = React.useCallback(
    (
      index: number,
      field: keyof Pick<PhotoRequirement, 'description' | 'required'>,
      value: string | boolean,
    ) => {
      onPhotoRequirementsChange((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    [onPhotoRequirementsChange],
  );

  React.useEffect(() => {
    if (hasInitialized.current) return;
    if (photoRequirements.length > 0) {
      hasInitialized.current = true;
      return;
    }

    hasInitialized.current = true;
    if (defaultPhotoRequirements && defaultPhotoRequirements.length > 0) {
      applyDefaultPhotoRequirements();
    } else {
      onPhotoRequirementsChange([
        { id: uuid(), description: '', required: false },
      ]);
    }
  }, [
    photoRequirements.length,
    defaultPhotoRequirements,
    applyDefaultPhotoRequirements,
    onPhotoRequirementsChange,
  ]);

  React.useEffect(() => {
    if (!hasInitialized.current) return;
    if (
      photoRequirements.length > 0 &&
      photoRequirements.at(-1)?.description !== ''
    ) {
      addPhotoRequirement();
    }
  }, [photoRequirements, addPhotoRequirement]);

  return (
    <div
      className={cn('@BookingPhotoRequirements', className)}
      style={{ contain: 'layout' }}
    >
      {photoRequirements.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={photoRequirements.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {photoRequirements.map((requirement, index) => {
                const isLastRow = index === photoRequirements.length - 1;

                if (isDisabled && isLastRow) {
                  return null;
                }

                return (
                  <PhotoRequirementRow
                    key={requirement.id}
                    requirement={requirement}
                    index={index}
                    isDisabled={isDisabled}
                    onUpdate={updatePhotoRequirement}
                    onRemove={removePhotoRequirement}
                    onFocus={
                      isLastRow && !isDisabled ? addPhotoRequirement : undefined
                    }
                    validationError={
                      validationErrors[`photoRequirement_${index}`]
                    }
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Col xCenter gap3 className="w-full py-12 text-center text-neutral/50">
          <p className="text-sm">
            {t.bookingsView.form.emptyStates.noPhotoRequirements}
          </p>

          {!isDisabled && (
            <Button
              variant="outline"
              color="primary"
              className="max-w-55"
              icon={<LucidePlus className="size-4" />}
              onPress={addPhotoRequirement}
            >
              {t.bookingsView.form.buttons.addPhotoRequirement}
            </Button>
          )}
        </Col>
      )}

      {!isDisabled && (!isSameAsDefault || !isDefaultPhotoRequirementsSet) && (
        <div className="flex items-center justify-between">
          <div className="mt-4">
            <Checkbox
              isSelected={useAsDefault}
              onChange={onUseAsDefaultChange}
              isDisabled={isDisabled}
            >
              {t.bookingsView.form.labels.useAsDefault}
            </Checkbox>
          </div>
          <div className="mt-4">
            {isDefaultPhotoRequirementsSet && !isSameAsDefault && (
              <Button
                variant="link"
                icon={<LucideRotateCcw className="size-4 text-primary" />}
                onPress={applyDefaultPhotoRequirements}
              >
                {t.bookingsView.form.labels.resetToDefault}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
